import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Printer, Trash2, Pencil, Send, Loader2,
  FileSpreadsheet, FileText, RefreshCw, Calendar, Search, X, CheckCircle,
  CheckCircle2, Upload, Download, MoreVertical, Lock, Unlock, FileCheck,
} from 'lucide-react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import EidResidenceRegistrations from '../components/EidResidenceRegistrations';
import { Ticket, Home } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EidZabhiyatPage({ embedded = false }) {
  const navigate = useNavigate();
  const [yearOptions, setYearOptions] = useState([]);  // detailed year objects
  const [yearsLoading, setYearsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedYearMeta, setSelectedYearMeta] = useState(null);
  const [showYearPicker, setShowYearPicker] = useState(true);
  const [customYear, setCustomYear] = useState('');
  const [showNewYearDialog, setShowNewYearDialog] = useState(false);

  // Reports / Complete Event dialog state
  const [reportsDialogYear, setReportsDialogYear] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [uploadingReport, setUploadingReport] = useState(false);
  const [completingYear, setCompletingYear] = useState(false);

  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingReg, setEditingReg] = useState(null);

  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  const api = axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });

  // Form state
  const [tokenInfo, setTokenInfo] = useState({ last: 0, suggested: 1, gaps: [] });
  const [form, setForm] = useState({ its: '', name: '', mobile: '', count: 1, tokens: [''] });
  const [saving, setSaving] = useState(false);

  // Dashboard stats
  const [stats, setStats] = useState({ majmui_zabhiyat: 0, residence_zabhiyat: 0, total: 0 });
  const [statsLoading, setStatsLoading] = useState(false);

  const loadStats = useCallback(async (year) => {
    if (!year) return;
    setStatsLoading(true);
    try {
      const res = await api.get(`/eid-zabhiyat/${encodeURIComponent(year)}/stats`);
      setStats(res.data);
    } catch {
      // silent — keep previous values
    } finally {
      setStatsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Member-search state (autocomplete inside New Registration dialog)
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [memberSearchOpen, setMemberSearchOpen] = useState(false);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [memberPicked, setMemberPicked] = useState(false);

  // Load only existing years on mount (no defaults)
  useEffect(() => {
    (async () => {
      setYearsLoading(true);
      try {
        const res = await api.get('/eid-zabhiyat/years');
        setYearOptions(res.data?.years || []);
      } catch (e) {
        // ignore — empty list
      } finally {
        setYearsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRegistrations = useCallback(async (year) => {
    if (!year) return;
    setLoading(true);
    try {
      const res = await api.get(`/eid-zabhiyat/${encodeURIComponent(year)}/registrations`);
      setRegs(res.data.registrations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const loadNextToken = useCallback(async (year) => {
    try {
      const res = await api.get(`/eid-zabhiyat/${encodeURIComponent(year)}/next-token`);
      setTokenInfo(res.data);
      return res.data;
    } catch {
      return { last: 0, suggested: 1, gaps: [] };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced member search: triggers fuzzy lookup as the user types
  useEffect(() => {
    if (!showAdd || editingReg) return;
    const term = memberSearchTerm.trim();
    if (term.length < 2) {
      setMemberSearchResults([]);
      setMemberSearchOpen(false);
      return;
    }
    setMemberSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(term)}&limit=10`);
        setMemberSearchResults(res.data?.members || []);
        setMemberSearchOpen(true);
      } catch {
        setMemberSearchResults([]);
      } finally {
        setMemberSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberSearchTerm, showAdd, editingReg]);

  const pickMember = (m) => {
    // Clean +91-style prefix from mobile, keep last 10 digits
    const digits = String(m.mobile || '').replace(/\D/g, '');
    const last10 = digits.length >= 10 ? digits.slice(-10) : digits;
    setForm(prev => ({
      ...prev,
      name: m.full_name || prev.name,
      its: (m.its_id || '').toString().replace(/\D/g, '').slice(0, 8) || prev.its,
      mobile: last10 || prev.mobile,
    }));
    setMemberSearchTerm(m.full_name || '');
    setMemberSearchOpen(false);
    setMemberPicked(true);
  };

  const clearPickedMember = () => {
    setMemberPicked(false);
    setMemberSearchTerm('');
    setMemberSearchResults([]);
    setMemberSearchOpen(false);
  };

  // Reset member-search state whenever the dialog opens or closes
  useEffect(() => {
    if (!showAdd) {
      setMemberSearchTerm('');
      setMemberSearchResults([]);
      setMemberSearchOpen(false);
      setMemberPicked(false);
    }
  }, [showAdd]);

  const pickYear = (y) => {
    const yearStr = typeof y === 'string' ? y : y.year;
    const meta = typeof y === 'string' ? yearOptions.find(o => o.year === y) : y;
    setSelectedYear(yearStr);
    setSelectedYearMeta(meta || null);
    setShowYearPicker(false);
    loadRegistrations(yearStr);
    loadStats(yearStr);
  };

  // ---- Year reports / complete-event handlers ----
  const refreshYears = async () => {
    try {
      const res = await api.get('/eid-zabhiyat/years');
      setYearOptions(res.data?.years || []);
    } catch {}
  };

  const openReportsDialog = async (yearObj) => {
    setReportsDialogYear(yearObj);
    setReportsLoading(true);
    setReports([]);
    try {
      const res = await api.get(`/eid-zabhiyat/years/${encodeURIComponent(yearObj.year)}/reports`);
      setReports(res.data?.reports || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load reports');
    } finally {
      setReportsLoading(false);
    }
  };

  const handleUploadReport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      e.target.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploadingReport(true);
    try {
      await api.post(
        `/eid-zabhiyat/years/${encodeURIComponent(reportsDialogYear.year)}/reports?title=${encodeURIComponent(file.name)}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Report uploaded');
      await openReportsDialog(reportsDialogYear);
      await refreshYears();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingReport(false);
      e.target.value = '';
    }
  };

  const handleDownloadReport = async (rep) => {
    try {
      const res = await api.get(
        `/eid-zabhiyat/years/${encodeURIComponent(reportsDialogYear.year)}/reports/${rep.id}/download`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = rep.original_filename || `eid_${reportsDialogYear.year}_report.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleDeleteReport = async (rep) => {
    if (!window.confirm(`Delete report "${rep.title || rep.original_filename}"?`)) return;
    try {
      await api.delete(`/eid-zabhiyat/years/${encodeURIComponent(reportsDialogYear.year)}/reports/${rep.id}`);
      toast.success('Report deleted');
      await openReportsDialog(reportsDialogYear);
      await refreshYears();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleCompleteYear = async (yearObj) => {
    if (!window.confirm(`Mark ${yearObj.year} as Completed?\nThe year becomes read-only — no new registrations, edits, or deletes will be allowed until re-opened.`)) return;
    setCompletingYear(true);
    try {
      await api.post(`/eid-zabhiyat/years/${encodeURIComponent(yearObj.year)}/complete`);
      toast.success(`${yearObj.year} marked Completed`);
      await refreshYears();
      // Refresh local meta if currently selected
      if (selectedYear === yearObj.year) {
        await loadStats(selectedYear);
        const res = await api.get('/eid-zabhiyat/years');
        const fresh = (res.data?.years || []).find(o => o.year === yearObj.year);
        setSelectedYearMeta(fresh || null);
      }
      if (reportsDialogYear?.year === yearObj.year) {
        const res = await api.get('/eid-zabhiyat/years');
        const fresh = (res.data?.years || []).find(o => o.year === yearObj.year);
        setReportsDialogYear(fresh || yearObj);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not complete year');
    } finally {
      setCompletingYear(false);
    }
  };

  const handleReopenYear = async (yearObj) => {
    if (!window.confirm(`Re-open ${yearObj.year} for edits?`)) return;
    try {
      await api.post(`/eid-zabhiyat/years/${encodeURIComponent(yearObj.year)}/reopen`);
      toast.success(`${yearObj.year} re-opened`);
      await refreshYears();
      if (selectedYear === yearObj.year) {
        const res = await api.get('/eid-zabhiyat/years');
        const fresh = (res.data?.years || []).find(o => o.year === yearObj.year);
        setSelectedYearMeta(fresh || null);
      }
      if (reportsDialogYear?.year === yearObj.year) {
        const res = await api.get('/eid-zabhiyat/years');
        const fresh = (res.data?.years || []).find(o => o.year === yearObj.year);
        setReportsDialogYear(fresh || yearObj);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not re-open');
    }
  };

  const openAdd = async () => {
    const info = await loadNextToken(selectedYear);
    setEditingReg(null);
    setForm({ its: '', name: '', mobile: '', count: 1, tokens: [String(info.suggested)] });
    setShowAdd(true);
  };

  const openEdit = (reg) => {
    setEditingReg(reg);
    setForm({
      its: reg.its || '',
      name: reg.name || '',
      mobile: reg.mobile || '',
      count: 1,
      tokens: [String(reg.token_no || '')],
    });
    setShowAdd(true);
  };

  const updateCount = (newCount) => {
    const n = Math.max(1, Math.min(50, parseInt(newCount, 10) || 1));
    setForm(prev => {
      const next = [...(prev.tokens || [])];
      // Compute suggested based on current tokens already chosen + DB last
      const usedSet = new Set([
        ...prev.tokens.map(t => parseInt(t, 10)).filter(Boolean),
      ]);
      while (next.length < n) {
        // suggest next free starting from suggested
        let candidate = (tokenInfo.last || 0) + 1;
        while (usedSet.has(candidate)) candidate += 1;
        usedSet.add(candidate);
        next.push(String(candidate));
      }
      while (next.length > n) next.pop();
      return { ...prev, count: n, tokens: next };
    });
  };

  const updateTokenAt = (idx, val) => {
    setForm(prev => {
      const tokens = [...prev.tokens];
      tokens[idx] = val.replace(/\D/g, '');
      return { ...prev, tokens };
    });
  };

  const submitForm = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const mob = (form.mobile || '').replace(/\D/g, '');
    if (mob.length !== 10) { toast.error('Mobile must be exactly 10 digits'); return; }
    if (form.its && !/^\d{8}$/.test(form.its)) { toast.error('ITS must be exactly 8 digits'); return; }

    const tokensArr = (form.tokens || []).map(t => parseInt(t, 10));
    if (tokensArr.some(t => !Number.isFinite(t) || t < 1)) {
      toast.error('All token numbers must be valid positive integers');
      return;
    }
    if (new Set(tokensArr).size !== tokensArr.length) {
      toast.error('Token numbers must be unique within this registration');
      return;
    }

    setSaving(true);
    try {
      if (editingReg) {
        await api.put(`/eid-zabhiyat/${encodeURIComponent(selectedYear)}/registrations/${editingReg.id}`, {
          its: form.its, name: form.name.trim(), mobile: mob, token_no: tokensArr[0],
        });
        toast.success('Registration updated');
      } else {
        const resp = await api.post(`/eid-zabhiyat/${encodeURIComponent(selectedYear)}/register`, {
          year: selectedYear, its: form.its, name: form.name.trim(), mobile: mob, tokens: tokensArr,
        });
        toast.success(`${tokensArr.length} registration(s) created. WhatsApp sent.`);
        // Auto-open combined receipt for print
        const firstReg = resp.data?.registrations?.[0];
        if (firstReg?.id) openReceipt(firstReg.id);
      }
      setShowAdd(false);
      loadRegistrations(selectedYear);
      loadStats(selectedYear);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.error(`Duplicate Token: ${detail}`);
      } else {
        toast.error(detail || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const printPdfBlob = (blob, fallbackName) => {
    // Reliable cross-browser PDF print using a hidden iframe.
    const url = URL.createObjectURL(blob);
    let iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = url;
    document.body.appendChild(iframe);
    const triggerPrint = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        // Fallback: download
        const a = document.createElement('a');
        a.href = url; a.download = fallbackName || 'document.pdf'; a.click();
      }
    };
    iframe.onload = () => setTimeout(triggerPrint, 600);
    // Safety: revoke after a while
    setTimeout(() => {
      try { URL.revokeObjectURL(url); } catch {}
      try { iframe.remove(); } catch {}
    }, 60_000);
  };

  const openReceipt = (regId) => {
    // Backend returns an HTML page with @page A5 + auto window.print().
    // We load it in a hidden iframe; its inline <script> triggers print using the embedded @page size,
    // so browsers force A5 paper size regardless of last-used setting.
    api.get(`/eid-zabhiyat/${encodeURIComponent(selectedYear)}/registrations/${regId}/receipt`, {
      responseType: 'text',
      headers: { Accept: 'text/html' },
    })
      .then((res) => {
        const html = res.data;
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.src = url;
        document.body.appendChild(iframe);
        setTimeout(() => {
          try { URL.revokeObjectURL(url); } catch {}
          try { iframe.remove(); } catch {}
        }, 60_000);
      })
      .catch(() => toast.error('Could not load receipt'));
  };

  const handleDelete = async (reg) => {
    if (!window.confirm(`Delete registration for Token #${reg.token_no} (${reg.name})?`)) return;
    try {
      await api.delete(`/eid-zabhiyat/${encodeURIComponent(selectedYear)}/registrations/${reg.id}`);
      toast.success('Deleted');
      loadRegistrations(selectedYear);
      loadStats(selectedYear);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleResend = async (reg) => {
    try {
      await api.post(`/eid-zabhiyat/${encodeURIComponent(selectedYear)}/registrations/${reg.id}/resend-whatsapp`);
      toast.success('WhatsApp resend triggered');
      setTimeout(() => loadRegistrations(selectedYear), 3500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/eid-zabhiyat/${encodeURIComponent(selectedYear)}/export?format=excel`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `EidZabhiyat_${selectedYear}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Excel export failed'); }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/eid-zabhiyat/${encodeURIComponent(selectedYear)}/export?format=pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `EidZabhiyat_${selectedYear}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF export failed'); }
  };

  const printList = async () => {
    try {
      const res = await api.get(`/eid-zabhiyat/${encodeURIComponent(selectedYear)}/export?format=pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      printPdfBlob(blob, `EidZabhiyat_${selectedYear}.pdf`);
    } catch { toast.error('Print failed'); }
  };

  // ---- Year picker ----
  if (showYearPicker) {
    const PickerWrap = ({ children }) => embedded ? (
      <div className="space-y-4">{children}</div>
    ) : (
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/events')} data-testid="eid-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Events
          </Button>
        </div>
        {children}
      </div>
    );

    return (
      <PickerWrap>
        <Card className="shadow-sm">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
              <Calendar className="w-6 h-6 text-emerald-700" />
            </div>
            <CardTitle className="text-xl">Eid ul Adha Zabhiyat Registration</CardTitle>
            <p className="text-sm text-slate-500 mt-1">Select a registered year to continue</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {yearsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
              </div>
            ) : yearOptions.length === 0 ? (
              <div className="border-2 border-dashed rounded-xl p-8 text-center text-slate-500">
                <p className="mb-3">No years registered yet.</p>
                <Button onClick={() => setShowNewYearDialog(true)} data-testid="eid-create-first-year-btn">
                  <Plus className="w-4 h-4 mr-1" /> Create First Year
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {yearOptions.map((y) => (
                    <div
                      key={y.year}
                      className={`relative rounded-lg border-2 transition-all hover:shadow-md ${
                        y.completed ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 bg-white hover:border-emerald-400'
                      }`}
                      data-testid={`eid-year-card-${y.year}`}
                    >
                      <button
                        type="button"
                        onClick={() => pickYear(y)}
                        className="w-full text-left p-4 pb-2"
                        data-testid={`eid-year-${y.year}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-2xl font-extrabold text-slate-800">{y.year}</div>
                          {y.completed && (
                            <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-slate-600">
                          <span><Ticket className="w-3 h-3 inline mr-1 text-emerald-600" />{y.majmui_zabhiyat ?? y.count ?? 0} Majmui</span>
                          <span><Home className="w-3 h-3 inline mr-1 text-amber-600" />{y.residence_zabhiyat ?? 0} Residence</span>
                          {y.report_count > 0 && (
                            <span><FileCheck className="w-3 h-3 inline mr-1 text-blue-600" />{y.report_count} Report{y.report_count > 1 ? 's' : ''}</span>
                          )}
                        </div>
                      </button>
                      <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-t border-slate-100 bg-slate-50/60 rounded-b-lg">
                        {!y.completed ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleCompleteYear(y); }}
                            disabled={completingYear}
                            className="text-xs h-7 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100"
                            data-testid={`eid-complete-${y.year}`}
                          >
                            <Lock className="w-3 h-3 mr-1" /> Complete Event
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleReopenYear(y); }}
                            className="text-xs h-7 text-amber-700 hover:text-amber-800 hover:bg-amber-100"
                            data-testid={`eid-reopen-${y.year}`}
                          >
                            <Unlock className="w-3 h-3 mr-1" /> Re-open
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); openReportsDialog(y); }}
                          className="text-xs h-7 text-blue-700 hover:text-blue-800 hover:bg-blue-100"
                          data-testid={`eid-reports-${y.year}`}
                        >
                          <FileText className="w-3 h-3 mr-1" /> Reports
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-center pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowNewYearDialog(true)}
                    data-testid="eid-new-year-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Start a new year
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Dialog open={showNewYearDialog} onOpenChange={(o) => !o && setShowNewYearDialog(false)}>
          <DialogContent className="max-w-sm" data-testid="eid-new-year-dialog">
            <DialogHeader>
              <DialogTitle>Start New Hijri Year</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Hijri Year (e.g., 1447H)</Label>
              <Input
                value={customYear}
                onChange={(e) => setCustomYear(e.target.value.toUpperCase())}
                placeholder="1447H"
                data-testid="eid-custom-year-input"
                autoFocus
              />
              <p className="text-xs text-slate-500">Once a token is created for a year, it will appear in the picker.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewYearDialog(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  const y = customYear.trim().toUpperCase();
                  if (!y) { toast.error('Enter a year'); return; }
                  try {
                    await api.post('/eid-zabhiyat/years', { year: y });
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Could not save year');
                    return;
                  }
                  await refreshYears();
                  setShowNewYearDialog(false);
                  setCustomYear('');
                  pickYear({ year: y, completed: false, majmui_zabhiyat: 0, residence_zabhiyat: 0, total: 0, report_count: 0 });
                }}
                disabled={!customYear.trim()}
                data-testid="eid-custom-year-go"
              >
                Continue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reports / Complete Event dialog */}
        <Dialog open={!!reportsDialogYear} onOpenChange={(o) => { if (!o) setReportsDialogYear(null); }}>
          <DialogContent className="max-w-lg" data-testid="eid-reports-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-700" />
                Reports — Eid ul Adha {reportsDialogYear?.year}
                {reportsDialogYear?.completed && (
                  <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Upload */}
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600 mb-2">Upload a PDF report for this year</p>
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium cursor-pointer hover:bg-blue-700">
                  {uploadingReport ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                  ) : (
                    <><Upload className="w-4 h-4" /> Choose PDF</>
                  )}
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={handleUploadReport}
                    disabled={uploadingReport}
                    className="hidden"
                    data-testid="eid-report-upload-input"
                  />
                </label>
                <p className="text-xs text-slate-400 mt-2">Max 25 MB. Multiple PDFs allowed.</p>
              </div>

              {/* Reports list */}
              <div>
                <div className="text-sm font-semibold text-slate-700 mb-2">Uploaded Reports</div>
                {reportsLoading ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
                ) : reports.length === 0 ? (
                  <div className="text-center text-sm text-slate-400 py-6 border rounded">
                    No reports uploaded yet.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {reports.map((r) => (
                      <div key={r.id} className="flex items-center gap-2 p-2 border rounded hover:bg-slate-50" data-testid={`eid-report-row-${r.id}`}>
                        <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate" title={r.title}>{r.title || r.original_filename}</div>
                          <div className="text-xs text-slate-500">
                            {r.uploaded_at ? new Date(r.uploaded_at).toLocaleString() : '-'}
                            {r.uploaded_by_name && <> · {r.uploaded_by_name}</>}
                            {typeof r.size === 'number' && <> · {(r.size / 1024).toFixed(0)} KB</>}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadReport(r)} title="Download" data-testid={`eid-report-download-${r.id}`}>
                          <Download className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteReport(r)} title="Delete" className="text-red-500" data-testid={`eid-report-delete-${r.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              {reportsDialogYear && !reportsDialogYear.completed ? (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleCompleteYear(reportsDialogYear)}
                  disabled={completingYear}
                  data-testid="eid-dialog-complete-btn"
                >
                  <Lock className="w-4 h-4 mr-1" /> Mark Event Completed
                </Button>
              ) : reportsDialogYear?.completed ? (
                <Button
                  variant="outline"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  onClick={() => handleReopenYear(reportsDialogYear)}
                  data-testid="eid-dialog-reopen-btn"
                >
                  <Unlock className="w-4 h-4 mr-1" /> Re-open Event
                </Button>
              ) : null}
              <Button variant="outline" onClick={() => setReportsDialogYear(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PickerWrap>
    );
  }

  // ---- Registrations table ----
  return (
    <div className={embedded ? "space-y-4" : "p-4 sm:p-6 space-y-4 max-w-7xl mx-auto"}>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => setShowYearPicker(true)} data-testid="eid-change-year-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Change Year
        </Button>
        <div className="flex-1">
          <h1 className={embedded ? "text-lg font-bold" : "text-2xl font-bold"}>
            Eid ul Adha {selectedYear} — Zabhiyat Registration
          </h1>
          {!embedded && <p className="text-sm text-slate-500">Anjuman e Saifee, Nagpur</p>}
        </div>
        {selectedYearMeta?.completed && (
          <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="eid-readonly-badge">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Completed · Read-only
          </Badge>
        )}
      </div>

      {/* Dashboard counters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="eid-dashboard">
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Ticket className="w-6 h-6 text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Majmui Zabhiyat</div>
              <div className="text-3xl font-extrabold text-emerald-900 leading-none mt-1" data-testid="stat-majmui">
                {statsLoading ? '…' : stats.majmui_zabhiyat}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Home className="w-6 h-6 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Residence Zabhiyat</div>
              <div className="text-3xl font-extrabold text-amber-900 leading-none mt-1" data-testid="stat-residence">
                {statsLoading ? '…' : stats.residence_zabhiyat}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-slate-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Total Zabhiyat</div>
              <div className="text-3xl font-extrabold text-slate-900 leading-none mt-1" data-testid="stat-total">
                {statsLoading ? '…' : stats.total}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="token" className="w-full">
        <TabsList>
          <TabsTrigger value="token" data-testid="eid-tab-token">
            <Ticket className="w-4 h-4 mr-1" /> Token Zabhiyat
          </TabsTrigger>
          <TabsTrigger value="residence" data-testid="eid-tab-residence">
            <Home className="w-4 h-4 mr-1" /> Residence Zabhiyat
          </TabsTrigger>
        </TabsList>

        <TabsContent value="token" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => loadRegistrations(selectedYear)} data-testid="eid-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportExcel} disabled={!regs.length} data-testid="eid-export-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={exportPdf} disabled={!regs.length} data-testid="eid-export-pdf-btn">
              <FileText className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={printList} disabled={!regs.length} data-testid="eid-print-list-btn">
              <Printer className="w-4 h-4 mr-1" /> Print List
            </Button>
            <Button onClick={openAdd} disabled={selectedYearMeta?.completed} data-testid="eid-add-btn">
              <Plus className="w-4 h-4 mr-1" /> New Token Registration
            </Button>
          </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : regs.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              No registrations yet for {selectedYear}. Click <strong>New Registration</strong> to add the first one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Token #</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>ITS</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>WhatsApp</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {regs.map(r => (
                  <TableRow key={r.id} data-testid={`eid-row-${r.token_no}`}>
                    <TableCell className="font-bold text-lg">#{r.token_no}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.its || '-'}</TableCell>
                    <TableCell>{r.mobile}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.registration_date ? new Date(r.registration_date).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.whatsapp_status === 'sent' ? 'default' : r.whatsapp_status === 'failed' ? 'destructive' : 'secondary'}>
                        {r.whatsapp_status || 'pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openReceipt(r.id)} title="Print receipt" data-testid={`eid-print-${r.token_no}`}>
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleResend(r)} title="Resend WhatsApp" data-testid={`eid-resend-${r.token_no}`}>
                          <Send className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} title="Edit" data-testid={`eid-edit-${r.token_no}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(r)} className="text-red-500" title="Delete" data-testid={`eid-delete-${r.token_no}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAdd} onOpenChange={(o) => !o && setShowAdd(false)}>
        <DialogContent className="max-w-md" data-testid="eid-add-dialog">
          <DialogHeader>
            <DialogTitle>{editingReg ? 'Edit Registration' : 'New Registration'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-4">
            {!editingReg && (
              <div className="relative">
                <Label className="flex items-center gap-1">
                  <Search className="w-3.5 h-3.5" /> Search Member (Name / ITS / Mobile)
                </Label>
                <div className="relative mt-1">
                  <Input
                    value={memberSearchTerm}
                    onChange={(e) => { setMemberSearchTerm(e.target.value); setMemberPicked(false); }}
                    onFocus={() => { if (memberSearchResults.length > 0) setMemberSearchOpen(true); }}
                    placeholder="Type to auto-fetch name, ITS & mobile"
                    className={memberPicked ? 'border-emerald-400 pr-10' : 'pr-10'}
                    data-testid="eid-form-member-search"
                  />
                  {memberSearchLoading && (
                    <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
                  )}
                  {!memberSearchLoading && memberPicked && (
                    <button
                      type="button"
                      onClick={clearPickedMember}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-emerald-600 hover:text-red-500"
                      title="Clear"
                      data-testid="eid-form-member-clear"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {!memberSearchLoading && !memberPicked && memberSearchTerm.length >= 2 && memberSearchOpen && (
                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
                      {memberSearchResults.length === 0 ? (
                        <div className="p-3 text-sm text-slate-500">No match. You can enter details manually below.</div>
                      ) : (
                        memberSearchResults.map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => pickMember(m)}
                            className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b last:border-b-0 border-slate-100"
                            data-testid={`eid-form-member-result-${m.its_id}`}
                          >
                            <div className="text-sm font-medium text-slate-800">{m.full_name}</div>
                            <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                              <span>ITS: {m.its_id || '-'}</span>
                              <span>{m.mobile || '-'}</span>
                              {m.mohalla && <span>{m.mohalla}</span>}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {memberPicked && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Member selected — fields below auto-filled. You can still edit them.
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-1">Or skip search and enter details manually below.</p>
              </div>
            )}
            <div>
              <Label>ITS Number (optional)</Label>
              <Input
                value={form.its}
                onChange={(e) => setForm({ ...form, its: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                placeholder="8-digit ITS"
                inputMode="numeric"
                data-testid="eid-form-its"
              />
            </div>
            <div>
              <Label>Full Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Member's name"
                required
                data-testid="eid-form-name"
              />
            </div>
            <div>
              <Label>Mobile Number * (10 digits)</Label>
              <Input
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                placeholder="98XXXXXXXX"
                inputMode="numeric"
                maxLength={10}
                required
                data-testid="eid-form-mobile"
              />
            </div>
            <div>
              <Label>No. of Zabhiyat *</Label>
              <Input
                type="number"
                min={1}
                max={50}
                value={form.count}
                onChange={(e) => updateCount(e.target.value)}
                disabled={!!editingReg}
                data-testid="eid-form-count"
              />
              {editingReg && <p className="text-xs text-slate-400 mt-1">Editing a single token row. Use a new registration to add more.</p>}
            </div>
            <div>
              <Label>Token Number{form.tokens.length > 1 ? 's' : ''} *</Label>
              {!editingReg && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 mb-2">
                  <div><strong>Last issued:</strong> #{tokenInfo.last || 0}</div>
                  <div><strong>Suggested next:</strong> #{tokenInfo.suggested}</div>
                  {tokenInfo.gaps?.length > 0 && (
                    <div className="mt-1">
                      <strong>Available gaps:</strong>{' '}
                      {tokenInfo.gaps.slice(0, 30).map(g => (
                        <button
                          type="button"
                          key={g}
                          onClick={() => {
                            // assign to first empty slot, else last
                            setForm(prev => {
                              const tokens = [...prev.tokens];
                              const idx = tokens.findIndex(t => !t);
                              const target = idx >= 0 ? idx : tokens.length - 1;
                              tokens[target] = String(g);
                              return { ...prev, tokens };
                            });
                          }}
                          className="inline-block mx-0.5 mb-0.5 px-2 py-0.5 rounded bg-white border border-emerald-300 hover:bg-emerald-100"
                          data-testid={`eid-gap-${g}`}
                        >
                          #{g}
                        </button>
                      ))}
                      {tokenInfo.gaps.length > 30 && <span className="text-slate-500"> …</span>}
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {form.tokens.map((tk, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-12">#{i + 1}</span>
                    <Input
                      value={tk}
                      onChange={(e) => updateTokenAt(i, e.target.value)}
                      placeholder={`Token ${i + 1}`}
                      inputMode="numeric"
                      required
                      data-testid={`eid-form-token-${i}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="text-xs text-slate-500">
              Registration date: <span className="font-medium">{new Date().toLocaleDateString()}</span> (auto)
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="eid-form-submit">
                {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>) : (editingReg ? 'Update' : 'Register & Print Receipt')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="residence" className="space-y-4 mt-4">
          <EidResidenceRegistrations
            year={selectedYear}
            readOnly={!!selectedYearMeta?.completed}
            onChange={() => loadStats(selectedYear)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
